export type WebsitePageKey = 'product' | 'integrations' | 'security' | 'partners';

type StandardsRow = {
  criterion: string;
  pain: string;
  help: string;
};

type PageCard = {
  title: string;
  description: string;
};

type CompareBlock = {
  leftTitle: string;
  leftItems: string[];
  rightTitle: string;
  rightItems: string[];
};

type QaItem = {
  question: string;
  answer: string;
};

export type SecurityPillar = {
  title: string;
  problem: string;
  solution: string;
  outcome: string;
};

export type SecurityPillarLabels = {
  problem: string;
  solution: string;
  outcome: string;
};

type PageSection = {
  title: string;
  body: string;
  anchorId?: string;
  useLandingDeploymentStyle?: boolean;
  quickStats?: Array<{
    label: string;
    value: string;
    hint: string;
  }>;
  officePresencePreview?: {
    title: string;
    subtitle: string;
    countLabel: string;
    people: string[];
  };
  payrollReportPreview?: {
    badge: string;
    title: string;
    subtitle: string;
    periodLabel: string;
    periodValue: string;
    colEmployee: string;
    colRegular: string;
    colOvertime: string;
    colNet: string;
    rows: Array<{ employee: string; regularHours: string; overtimeHours: string; netHours: string }>;
    totalLabel: string;
    totalValue: string;
    statusLabel: string;
  };
  reportsWorkbenchPreview?: {
    badge: string;
    title: string;
    subtitle: string;
    valueTitle: string;
    metrics: Array<{ label: string; value: string }>;
    exportLabel: string;
    exportFormats: string[];
    readyStatus: string;
  };
  technicalSnapshot?: {
    badge: string;
    title: string;
    points: string[];
    sampleTitle: string;
    sampleLines: string[];
  };
  cards?: PageCard[];
  bullets?: string[];
  compare?: CompareBlock;
  qa?: QaItem[];
  pillars?: SecurityPillar[];
  pillarLabels?: SecurityPillarLabels;
  cardsColumns?: 2 | 3;
  standardsTable?: {
    headers: [string, string, string];
    rows: StandardsRow[];
  };
};

type WebsitePage = {
  metadata: {
    title: string;
    description: string;
  };
  teaser: {
    title: string;
    description: string;
    cta: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    highlights?: string[];
    primaryCta: string;
    secondaryCta: string;
    secondaryHref: string;
  };
  sections: PageSection[];
  finalCta: {
    title: string;
    body: string;
    primaryCta: string;
    qualifierChips?: string[];
  };
};

type WebsitePagesCopy = {
  brand: string;
  nav: {
    home: string;
    product: string;
    integrations: string;
    partners: string;
    security: string;
    privacy: string;
    terms: string;
    contact: string;
    signIn: string;
    bookDemo: string;
  };
  pages: Record<WebsitePageKey, WebsitePage>;
};

const srCopy: WebsitePagesCopy = {
  brand: 'Cadrino ERV',
  nav: {
    home: 'Početna',
    product: 'Proizvod',
    integrations: 'Cadrino HR',
    partners: 'Partneri',
    security: 'Sigurnost',
    privacy: 'Politika privatnosti',
    terms: 'Uslovi korišćenja',
    contact: 'Kontakt',
    signIn: 'Prijava',
    bookDemo: 'Zakažite demo',
  },
  pages: {
    product: {
      metadata: {
        title: 'Cadrino ERV Proizvod, prisustvo, pristup, uređaji i izveštaji',
        description:
          'Jedan sistem za evidenciju, kontrolu pristupa, uređaje i izveštaje spremne za obračun. Jednostavno za tim, ozbiljno za firmu.',
      },
      teaser: {
        title: 'Kompletan ERV sistem bez komplikacija',
        description:
          'Prisustvo, pristup, uređaji, obrada podataka i ključni izveštaji u jednom pregledu koji tim zaista koristi.',
        cta: 'Otvori Proizvod',
      },
      hero: {
        eyebrow: 'Proizvod',
        title: 'Jednostavno za tim. Ozbiljno za firmu.',
        subtitle:
          'Cadrino ERV spaja evidenciju prisustva, kontrolu pristupa i rad uređaja u jedan jasan sistem. HR, operativa i rukovodstvo gledaju istu sliku, svakog dana, ne samo na kraju meseca.',
        highlights: [
          'prisustvo i pristup na jednom mestu',
          'uređaji pod kontrolom u realnom vremenu',
          'izveštaji spremni za obračun',
          'ključ u ruke ili vaša infrastruktura',
        ],
        primaryCta: 'Zakažite demo proizvoda',
        secondaryCta: 'Pogledajte Cadrino HR',
        secondaryHref: '/integrations',
      },
      sections: [
        {
          title: 'Šta vas košta bez jednog operativnog pregleda',
          body:
            'Većina firmi ima uređaje i neku evidenciju, ali ne i odgovor na pitanja koja se postavljaju svakog dana. Cadrino ERV uvodi jednu istinu za ceo tim.',
          officePresencePreview: {
            title: 'Trenutno u kancelariji',
            subtitle: 'IN bez OUT događaja',
            countLabel: 'aktivno prisutnih',
            people: ['Marko Jovanović', 'Ana Nikolić', 'Stefan Đorđević', 'Jelena Popović'],
          },
          standardsTable: {
            headers: ['Svakodnevno pitanje', 'Bez jedinstvenog sistema', 'Sa Cadrino ERV'],
            rows: [
              {
                criterion: 'Ko je danas prisutan?',
                pain: 'Provera kroz više tabela, poruka i pretpostavki',
                help: 'Jasan pregled prisustva po lokaciji, timu ili uređaju',
              },
              {
                criterion: 'Ko sme na koji ulaz?',
                pain: 'Pristup preširok ili pogrešan, greške i rizik',
                help: 'Pravila po zaposlenom, uređaju, ulazu ili lokaciji',
              },
              {
                criterion: 'Da li uređaj radi?',
                pain: 'Problem se vidi tek kad izveštaj „ne štima“',
                help: 'Status uređaja i šta čeka obradu, odmah vidljivo',
              },
              {
                criterion: 'Šta ide u obračun?',
                pain: 'Ručno spajanje podataka pre plate',
                help: 'Izveštaji i export iz jednog izvora, bez Excel haosa',
              },
            ],
          },
        },
        {
          title: 'Šta Cadrino ERV pokriva u jednom sistemu',
          body:
            'Ne prodajemo „još jednu evidenciju“. Prodajemo kontrolu nad prisustvom, pristupom i uređajima, sa jasnom obradom i izveštajima koje tim zaista koristi.',
          pillarLabels: {
            problem: 'Šta vas muči',
            solution: 'Šta Cadrino ERV radi',
            outcome: 'Šta dobijate',
          },
          pillars: [
            {
              title: 'Evidencija prisustva',
              problem: 'Dolasci i odlasci se proveravaju na više mesta.',
              solution: 'Svi događaji sa uređaja idu u jedan pregled sa jasnim statusom obrade.',
              outcome: 'Manje ručnog rada i manje rasprava oko sati.',
            },
            {
              title: 'Kontrola pristupa',
              problem: 'Neko ima pristup svuda, neko nigde, haos pri promenama.',
              solution: 'Određujete ko sme na koje uređaje, ulaze i lokacije.',
              outcome: 'Red na ulazu, manje bezbednosnih i organizacionih grešaka.',
            },
            {
              title: 'Uređaji pod kontrolom',
              problem: 'Ne znate da li uređaj ne radi dok ne zaboli izveštaj.',
              solution: 'Vidite status uređaja, poslednju vezu i šta je zabeleženo.',
              outcome: 'Reagujete pre praznine u evidenciji.',
            },
            {
              title: 'Program u vašoj mreži',
              problem: 'Strah da uređaji „žive“ na internetu ili da podaci nestanu.',
              solution: 'Veza preko programa u firmi; događaji čekaju obradu ako veza stane.',
              outcome: 'Kontrola ostaje kod vas, bez crne kutije.',
            },
            {
              title: 'Obrada i red čekanja',
              problem: '„Da li je prošlo?“, niko ne zna šta čeka ili zašto.',
              solution: 'Jasan red događaja: obrađeno, na čekanju, za proveru.',
              outcome: 'Tim zna gde da reaguje, bez IT prevodioca.',
            },
            {
              title: 'Više lokacija, jedan pregled',
              problem: 'Svaka lokacija ima svoju tabelu i svoju istinu.',
              solution: 'Isti sistem za sve objekte, ulaze i uređaje u firmi.',
              outcome: 'Rukovodstvo i HR gledaju celu sliku, ne fragmente.',
            },
          ],
        },
        {
          title: 'Pregled koji tim otvara svakog dana',
          body:
            'Cadrino ERV nije „sistem za kraj meseca“. To je alat za operativu, HR i rukovodstvo tokom celog dana.',
          quickStats: [
            {
              label: 'Kašnjenja danas',
              value: '11',
              hint: 'zaposlenih kasni 5+ min',
            },
            {
              label: 'Raniji odlasci',
              value: '6',
              hint: 'odlazak pre kraja smene',
            },
            {
              label: 'Događaji na čekanju',
              value: '4',
              hint: 'čeka proveru ili ponovno slanje',
            },
            {
              label: 'Ulazi van pravila',
              value: '3',
              hint: 'pokušaji bez dozvole',
            },
          ],
          cards: [
            {
              title: 'Uređaji',
              description:
                'Gde su, da li rade, kada su se javili i šta je poslednje zabeleženo, bez pozivanja IT-a.',
            },
            {
              title: 'Ko sme gde',
              description:
                'Prava pristupa po zaposlenom, uređaju ili lokaciji. Promene su vidljive, ne skrivene u tabeli.',
            },
            {
              title: 'Šta čeka obradu',
              description:
                'Događaji na čekanju, ponovnom slanju ili proveri, da niko ne nagađa da li je evidencija prošla.',
            },
          ],
        },
        {
          title: 'Ne verujete samo izveštaju: vidite šta je zabeleženo',
          body:
            'Kada uređaj i izveštaj ne kažu isto, problem ne nestaje. Upoređujete stvarno stanje sa pregledom pre obračuna i rešavate razliku dok je mala.',
          bullets: [
            'Provera pre plate, ne posle rasprave',
            'Manje „čiji broj je tačan?“ na kraju meseca',
            'Ista baza za operativu, HR i rukovodstvo',
          ],
          payrollReportPreview: {
            badge: 'Izveštaj spreman za obračun',
            title: 'Mesečni obračun radnih sati',
            subtitle: 'Format koji HR i knjigovodstvo mogu odmah da koriste za kalkulaciju plata.',
            periodLabel: 'Period',
            periodValue: 'Maj 2026',
            colEmployee: 'Zaposleni',
            colRegular: 'Redovan',
            colOvertime: 'Prekov.',
            colNet: 'Neto sati',
            rows: [
              { employee: 'Marko Jovanović', regularHours: '176h', overtimeHours: '8h', netHours: '184h' },
              { employee: 'Ana Nikolić', regularHours: '168h', overtimeHours: '4h', netHours: '172h' },
              { employee: 'Stefan Đorđević', regularHours: '174h', overtimeHours: '6h', netHours: '180h' },
            ],
            totalLabel: 'Ukupno neto sati',
            totalValue: '536h',
            statusLabel: 'Spremno za slanje knjigovodstvu',
          },
        },
        {
          title: 'Izveštaji koji zatvaraju mesec bez Excel spajanja',
          body:
            'Pored svakodnevnog pregleda, dobijate izveštaje koje HR i računovodstvo zaista koriste. Navodimo ključne; u praksi ih ima znatno više, uključujući prilagođene po zahtevu vaše firme.',
          reportsWorkbenchPreview: {
            badge: 'Payroll workbench',
            title: 'Pregled pre slanja knjigovodstvu',
            subtitle: 'Jedan ekran za finalnu proveru i slanje obračuna, bez ručnog spajanja izvora.',
            valueTitle: 'Dodatna vrednost',
            metrics: [
              { label: 'Kasni dolasci (mesec)', value: '37' },
              { label: 'Raniji odlasci', value: '19' },
              { label: 'Noćni sati', value: '42h' },
              { label: 'Prekovremeni sati', value: '18h' },
            ],
            exportLabel: 'Izvoz za obračun',
            exportFormats: ['XLSX', 'CSV', 'PDF'],
            readyStatus: 'Spremno za slanje knjigovodstvu',
          },
          cardsColumns: 2,
          cards: [
            {
              title: 'Mesečni karnet i pregled prisustva',
              description:
                'Pregled dolazaka, odlazaka i sati po zaposlenom i periodu, spreman za dalju obradu i obračun.',
            },
            {
              title: 'Kašnjenja, raniji odlasci i odsustva',
              description:
                'Uočite obrasce pre nego što postanu spor oko sati ili discipline.',
            },
            {
              title: 'Prisustvo i pristup po lokaciji',
              description:
                'Ko je bio gde, ko je imao pravo ulaska, po objektu, ulazu ili uređaju.',
            },
            {
              title: 'Export za obračun i arhivu',
              description:
                'Podaci u formatu pogodnom za HR, računovodstvo i internu kontrolu, bez ručnog kopiranja iz više izvora.',
            },
          ],
          bullets: [
            'i mnogi drugi standardni izveštaji za svakodnevni i mesečni rad',
            'prilagođeni izveštaji po zahtevu klijenta, kada vam treba specifičan pogled na podatke',
            'analitika i dodatni pregledi, širimo ponudu u skladu sa potrebama firmi',
          ],
        },
        {
          title: 'Dva načina da krenete (oba sa punom podrškom)',
          body:
            'Biramo model prema vašem stvarnom stanju. Na demo-u kažemo iskreno šta vam treba, bez prodaje onoga što nećete koristiti.',
          useLandingDeploymentStyle: true,
          cardsColumns: 2,
          cards: [
            {
              title: 'Ključ u ruke',
              description:
                'Kompletno rešenje: uređaji najnovije generacije, softver, mreža, uvođenje i primopredaja. Idealno kada krećete od nule ili želite sve iz jednog izvora.',
            },
            {
              title: 'Postojeća infrastruktura',
              description:
                'Cadrino ERV na vašim uređajima i mreži. Automatizacija evidencije i pristupa bez zamene svega odjednom, širenje kada tim bude spreman.',
            },
          ],
          bullets: [
            'besplatna obuka nakon puštanja u rad',
            'besplatna korisnička i tehnička podrška',
            'opciono povezivanje sa Cadrino HR kada vam treba pun HR tok',
          ],
        },
        {
          title: 'Zašto firme biraju Cadrino ERV',
          body:
            'Firme nas biraju kada žele da zaustave curenje novca kroz kašnjenja, pogrešan pristup i ručno spajanje podataka. Ovo su četiri tačke gde se razlika najviše vidi.',
          pillarLabels: {
            problem: 'Gde novac odlazi',
            solution: 'Kako Cadrino ERV rešava',
            outcome: 'Zašto je to bolje od generičkog rešenja',
          },
          pillars: [
            {
              title: 'Kašnjenja i raniji odlasci',
              problem: 'Sati se „pojedu“ kroz male dnevne odstupanja koja se kasno vide.',
              solution: 'Kašnjenja, raniji odlasci i obrasci prisustva su odmah vidljivi po timu i lokaciji.',
              outcome: 'Reagujete u toku meseca, ne tek kada obračun pokaže manjak ili višak.',
            },
            {
              title: 'Pristup koji pravi rizik',
              problem: 'Preširok ili pogrešan pristup dovodi do grešaka, zastoja i bezbednosnog rizika.',
              solution: 'Pravila pristupa su jasna po zaposlenom, uređaju, ulazu i lokaciji, uz trag promena.',
              outcome: 'Manje incidenta i manje ad-hoc „otključavanja“ koje posle niko ne prati.',
            },
            {
              title: 'Ručni rad pre plata',
              problem: 'HR i računovodstvo gube sate na spajanje tabela pre svakog obračuna.',
              solution: 'Jedan izvor podataka + izveštaji i export spremni za obračun bez ručnog mosta.',
              outcome: 'Brže zatvaranje meseca i manje grešaka koje direktno koštaju firmu.',
            },
            {
              title: 'Sistem koji tim ne koristi',
              problem: 'Ako je alat komplikovan, operativa ga zaobilazi i vraća se na poruke i Excel.',
              solution: 'Interfejs je praktičan za svakodnevni rad HR-a, operative i rukovodstva.',
              outcome: 'Sistem se stvarno koristi svaki dan, pa podaci ostaju tačni i upotrebljivi.',
            },
          ],
        },
        {
          title: 'Pre i posle: da li je ovo sistem za vas?',
          body:
            'Ako se prepoznajete levo, desna kolona je ono što firme žele kada uvedu Cadrino ERV.',
          compare: {
            leftTitle: 'Haos podataka',
            leftItems: [
              'Excel, poruke i „proveri na uređaju“',
              'pristup se menja ručno i kasno se vidi greška',
              'uređaj ne radi, saznaju na kraju meseca',
              'kraj meseca = noćno spajanje tabela',
              'kašnjenja i raniji odlasci prolaze ispod radara',
              'prekovremeni sati ulaze u obračun bez jasne kontrole',
              'rukovodstvo dobija brojke tek kada je kasno za reakciju',
            ],
            rightTitle: 'Cadrino ERV',
            rightItems: [
              'jedan pregled: prisustvo, pristup, uređaji',
              'jasna pravila i trag promena',
              'status uređaja i red čekanja odmah',
              'izveštaji i export bez ručnog mosta',
              'kašnjenja i odlasci vidljivi tokom meseca, ne tek na kraju',
              'obračun sati sa proverljivom bazom i manje sporova oko plata',
              'rukovodstvo prati trendove i reaguje pre nego što trošak poraste',
            ],
          },
        },
        {
          title: 'Standard koji ozbiljna firma proverava pre odluke',
          body:
            'Pre izbora sistema postavite ova pitanja. Cadrino ERV je zamišljen da na njih odgovorite potvrdno.',
          anchorId: 'standard',
          standardsTable: {
            headers: ['Kriterijum', 'Uobičajena situacija', 'Kako Cadrino ERV pomaže'],
            rows: [
              {
                criterion: 'Ko sme gde',
                pain: 'Pristup napamet ili preširoko',
                help: 'Pravila po uređaju, ulazu, lokaciji',
              },
              {
                criterion: 'Šta je zabeleženo',
                pain: 'Izveštaj i uređaj ne slažu se',
                help: 'Uporedba i provera pre obračuna',
              },
              {
                criterion: 'Status uređaja',
                pain: 'Kvar se vidi prekasno',
                help: 'Pregled uređaja i obrade u realnom vremenu',
              },
              {
                criterion: 'Izveštaji',
                pain: 'Ručno spajanje pre plate',
                help: 'Karnet, kašnjenja, export i prilagođeni izveštaji po zahtevu',
              },
              {
                criterion: 'Uvođenje',
                pain: '„Sami ćemo“ bez plana i podrške',
                help: 'Demo, plan, obuka i besplatna podrška nakon puštanja',
              },
            ],
          },
        },
        {
          title: 'Česta pitanja o proizvodu',
          body: 'Odgovori pre demo razgovora, direktno i bez floskula.',
          qa: [
            {
              question: 'Da li je sistem komplikovan za svakodnevni rad?',
              answer:
                'Ne. Fokus je na jasnom pregledu koji operativa i HR koriste svakog dana, bez tehničkog žargona. Na demo prolazimo vaš scenario korak po korak.',
            },
            {
              question: 'Šta ako već imamo uređaje?',
              answer:
                'Uvodimo Cadrino ERV na postojeću infrastrukturu. Nije obavezna zamena opreme od prvog dana, širite kada vidite vrednost.',
            },
            {
              question: 'Koje izveštaje dobijamo?',
              answer:
                'Karnet i pregled prisustva, kašnjenja i odsustva, pregled po lokacijama, export za obračun, plus mnogi drugi standardni izveštaji i mogućnost prilagođenih po zahtevu vaše firme.',
            },
            {
              question: 'Da li moramo odmah i Cadrino HR?',
              answer:
                'Ne. ERV radi samostalno. Integracija sa Cadrino HR je opcija kada želite jedan tok od evidencije do HR-a i obračuna.',
            },
            {
              question: 'Možemo li krenuti sa jednom lokacijom?',
              answer:
                'Da. Mnoge firme kreću sa jednim delom poslovanja i proširuju sistem kada tim vidi rezultat, bez prekida rada.',
            },
          ],
        },
      ],
      finalCta: {
        title: 'Hoćete da vidite da li je Cadrino ERV idealan baš za vas?',
        body:
          'Na demo proizvoda prolazimo vaše lokacije, uređaje, pravila pristupa i izveštaje koje vam trebaju, sa realnim planom uvođenja, besplatnom obukom i besplatnom korisničkom i tehničkom podrškom nakon puštanja u rad.',
        primaryCta: 'Zakažite demo proizvoda',
      },
    },
    integrations: {
      metadata: {
        title: 'Cadrino ERV + HR, jedan proizvod, jedan tok podataka',
        description:
          'Prisustvo, pristup i uređaji sa Cadrino ERV. Zaposleni, odsustva i obračun sa Cadrino HR. Povezani, bez duplog unosa i ručnog prepisivanja.',
      },
      teaser: {
        title: 'ERV i HR kao jedan proizvod',
        description:
          'Jedan operativni tok od ulaza do obračuna, zaposleni, odsustva i evidencija na istoj, proverljivoj bazi.',
        cta: 'Otvori Cadrino HR',
      },
      hero: {
        eyebrow: 'Cadrino HR',
        title: 'Jedan proizvod. Dva dela. Jedan tok.',
        subtitle:
          'Cadrino ERV vodi prisustvo, pristup i uređaje na terenu. Cadrino HR vodi zaposlene, ugovore, odsustva i dokumentaciju. Kada ih povežete, rade kao jedan sistem, bez Excela između operativnog i HR tima.',
        highlights: [
          'jedan tok podataka',
          'bez duplog unosa',
          'ERV prvo, HR kada ste spremni',
        ],
        primaryCta: 'Zakažite demo ERV + HR',
        secondaryCta: 'Pogledajte Proizvod',
        secondaryHref: '/product',
      },
      sections: [
        {
          title: 'Šta vas košta kada sistemi ne rade zajedno',
          body:
            'Najčešći problem nije nedostatak funkcija, već što operativa i HR gledaju različite podatke i na kraju meseca spajaju istinu ručno.',
          standardsTable: {
            headers: ['Bolna tačka', 'Kako ERV + HR pomažu', 'Šta dobijate'],
            rows: [
              {
                criterion: 'Dupli unos zaposlenih',
                pain: 'Isti čovek u više tabela, greške i gubljenje vremena',
                help: 'Zaposleni i struktura ulaze jednom, vide se u oba sistema',
              },
              {
                criterion: 'Odsustva koje evidencija ne zna',
                pain: 'Odobreno u HR-u, na ulazu i u izveštaju drugačije',
                help: 'Odobrena odsustva se poštuju u evidenciji, manje korekcija',
              },
              {
                criterion: 'Kraj meseca = ručno spajanje',
                pain: 'Excel, poruke, „čiji je broj tačan?“',
                help: 'Evidencija sa uređaja ide dalje u HR na istoj bazi',
              },
              {
                criterion: 'Dva tima, dve istine',
                pain: 'Operativa i HR ne veruju istom izvoru',
                help: 'Jedan operativni tok, ista slika za sve',
              },
            ],
          },
        },
        {
          title: 'Jedan tok: od ulaza do obračuna',
          body:
            'Svaki korak rešava jednu stvarnu prepreku između terena i HR-a, bez dodatnog administrativnog rada.',
          pillarLabels: {
            problem: 'Bez povezivanja',
            solution: 'Sa ERV + HR',
            outcome: 'Ishod',
          },
          pillars: [
            {
              title: 'Zaposleni i struktura',
              problem: 'Novi zaposleni se unose na više mesta, podaci se raziđu.',
              solution: 'Jedan unos, ista osoba u evidenciji i u HR modulima.',
              outcome: 'Manje grešaka na ulazu i u izveštajima.',
            },
            {
              title: 'Odsustva',
              problem: 'Bolovanje odobreno u HR-u, a evidencija i dalje „vidi“ prisustvo.',
              solution: 'Odsustva se automatski poštuju tamo gde su potrebna u evidenciji.',
              outcome: 'Manje rasprava i ručnih ispravki između timova.',
            },
            {
              title: 'Evidencija → obračun',
              problem: 'Podaci sa uređaja se ručno prebacuju u HR pre obračuna.',
              solution: 'Zabeleženo na uređajima stiže u HR spremno za dalju obradu.',
              outcome: 'Brži kraj meseca, manje administracije.',
            },
            {
              title: 'Uvođenje po vašem ritmu',
              problem: 'Strah da morate odmah sve sisteme odjednom.',
              solution: 'Počinjete sa ERV-om; povezivanje sa HR aktivirate kada ste spremni.',
              outcome: 'Bez prekida rada i bez pritiska na „big bang“ migraciju.',
            },
          ],
        },
        {
          title: 'Ko šta vodi i kako se spaja',
          body:
            'Dva jasna dela jednog proizvoda. Operativa na terenu, HR u kancelariji, ista istina u pozadini.',
          cards: [
            {
              title: 'Cadrino ERV',
              description:
                'Prisustvo, pristup, uređaji i status obrade u realnom vremenu. Fokus ovog sajta, može samostalno, bez HR paketa.',
            },
            {
              title: 'Cadrino HR',
              description:
                'Zaposleni, ugovori, odsustva, dokumentacija i priprema za obračun. Kompletan HR modul na cadrinohr.com.',
            },
            {
              title: 'Zajedno',
              description:
                'Automatska razmena ljudi, odsustava i evidencije. Jedan tok podataka, manje duplog unosa, manje grešaka, brži obračun.',
            },
          ],
        },
        {
          title: 'Zašto firme biraju ERV + HR kao jedan proizvod',
          body:
            'Ne traže još jednu integraciju na papiru. Traže da operativa i HR prestanu da se prepisuju i da kraj meseca bude predvidiv.',
          bullets: [
            'Operativni tim radi evidenciju; HR radi ljude i obračun, bez ručnog mosta između',
            'Odsustva i prisustvo na istoj, proverljivoj bazi',
            'Evidencija sa uređaja ide dalje bez „spajanja tabela“',
            'Počinjete sa ERV-om; HR povezujete kada imate stvarnu potrebu, bez obaveze od prvog dana',
          ],
        },
        {
          title: 'Pre i posle, kako izgleda svakodnevica',
          body:
            'Levo je ono što većina firmi prepoznaje. Desno je ono što dobijate kada ERV i HR rade kao jedan proizvod.',
          compare: {
            leftTitle: 'Razdvojeni sistemi',
            leftItems: [
              'zaposleni u više tabela i fajlova',
              'odsustva u HR-u, evidencija ne zna',
              'kraj meseca = Excel i poruke',
              'operativa i HR ne veruju istom broju',
            ],
            rightTitle: 'ERV + HR, jedan tok',
            rightItems: [
              'zaposleni jednom, vidljivi svuda gde treba',
              'odsustva poštovana u evidenciji',
              'evidencija spremna za HR i obračun',
              'ista slika za operativu, HR i rukovodstvo',
            ],
          },
        },
        {
          title: 'Česta pitanja o integraciji',
          body:
            'Odgovori pre odluke, jasno šta je obavezno, a šta opciono.',
          qa: [
            {
              question: 'Da li moram odmah i Cadrino HR?',
              answer:
                'Ne. Cadrino ERV radi samostalno za prisustvo, pristup i uređaje. Integraciju sa Cadrino HR uključujete kada firma ima potrebu za punim HR tokom.',
            },
            {
              question: 'Šta tačno ide iz ERV-a u HR?',
              answer:
                'Zaposlene i strukturu, odsustva gde su relevantna za evidenciju, i podatke sa uređaja spremne za dalju obradu u HR-u, bez ručnog prepisivanja.',
            },
            {
              question: 'Možemo li prvo samo ERV, pa HR kasnije?',
              answer:
                'Da. To je čest put: operativni sloj prvo, povezivanje sa HR kada tim vidi vrednost i kada ste spremni za sledeći korak.',
            },
            {
              question: 'Da li je ovo „još jedna integracija“ koja puca?',
              answer:
                'Ne prodajemo spoljnu integraciju na papiru. ERV i HR su deo iste porodice proizvoda, dizajnirani da rade zajedno kao jedan tok, ne kao dva nepovezana alata.',
            },
          ],
        },
      ],
      finalCta: {
        title: 'Hoćete da vidite ERV i HR kao jedan proizvod kod vas?',
        body:
          'Na demo ERV + HR prolazimo vaš scenario: uređaji i pravila u ERV-u, šta ide u HR, kako izgleda tok zaposlenih i odsustava, i plan uvođenja, korak po korak, bez obaveze na HR od prvog dana.',
        primaryCta: 'Zakažite demo ERV + HR',
      },
    },
    partners: {
      metadata: {
        title: 'Partneri i Integracije, povežite vaš sistem sa Cadrino ERV',
        description:
          'Ako razvijate knjigovodstveni softver, ERP ili interno poslovno rešenje, Cadrino ERV može da bude pouzdan izvor podataka bez duplog unosa i ručnog prepisivanja.',
      },
      teaser: {
        title: 'Partner ekosistem za knjigovodstvo i ERP',
        description:
          'Jedan izvor operativne istine iz ERV-a za obračun, kontrolu i automatizaciju u vašem proizvodu.',
        cta: 'Otvori Partnere',
      },
      hero: {
        eyebrow: 'Partneri i Integracije',
        title: 'Povežite Cadrino ERV sa vašim sistemom. Bez duplog unosa.',
        subtitle:
          'Ako gradite knjigovodstveni softver, ERP ili interno poslovno rešenje, Cadrino ERV može da bude pouzdan izvor podataka o prisustvu, pristupu i događajima sa uređaja.',
        highlights: ['knjigovodstveni programi', 'ERP sistemi', 'interne aplikacije', 'API i eksport modeli'],
        primaryCta: 'Prijavite partnersku integraciju',
        secondaryCta: 'Pogledajte Proizvod',
        secondaryHref: '/product',
      },
      sections: [
        {
          title: 'Ko se najčešće integriše sa Cadrino ERV',
          body:
            'Najviše vrednosti dobijaju timovi koji žele automatizovan tok od evidencije do obračuna, bez ručnog rada i gubitka konteksta između sistema.',
          cards: [
            {
              title: 'Knjigovodstveni programi',
              description:
                'Preuzimanje obračunskih podataka bez ručnog unosa i naknadnog usklađivanja tabela.',
            },
            {
              title: 'ERP sistemi',
              description:
                'Povezivanje prisustva i organizacione strukture sa vašim postojećim procesima i modulima.',
            },
            {
              title: 'Interna poslovna rešenja',
              description:
                'Integracija prema vašoj logici, formatu i ritmu sinhronizacije, bez nametanja šablona.',
            },
          ],
        },
        {
          title: 'Podaci koji imaju smisla za obračun i operativu',
          body:
            'Integracija nije “sirov log bez konteksta”. Fokus je na podacima koje timovi zaista koriste za obračun, kontrolu i reviziju.',
          bullets: [
            'ulazi i izlazi sa vremenskim oznakama',
            'status obrade događaja (obrađeno, na čekanju, za proveru)',
            'agregati po zaposlenom, periodu i lokaciji',
            'izuzeci za proveru: kašnjenja, raniji odlasci, nelogičnosti',
            'podaci spremni za obračunski eksport',
          ],
        },
        {
          title: 'Tehnički snapshot, šta je obavezno, a šta opciono',
          body:
            'Pre početka jasno definišemo minimum za pilot i šta ostavljamo kao opcioni sledeći korak, da effort bude predvidiv.',
          technicalSnapshot: {
            badge: 'Technical snapshot',
            title: 'Brza procena bez tehničkog nagađanja',
            points: [
              'Obavezno: autentikacija, mapiranje zaposlenog, vreme događaja, tip događaja.',
              'Opcionalno: real-time tok, prošireni atributi, BI eksport i dodatni audit metapodaci.',
              'Pilot obim: jedna lokacija, jedan obračunski period, dogovoren format izlaza.',
            ],
            sampleTitle: 'Primer event payload-a',
            sampleLines: [
              '{',
              '  "employeeId": "EMP-1042",',
              '  "eventType": "IN",',
              '  "eventAt": "2026-05-28T07:58:14Z",',
              '  "deviceId": "dev-main-01",',
              '  "location": "Novi Sad - Glavni ulaz"',
              '}',
            ],
          },
        },
        {
          title: 'Model integracije prema vašem proizvodu',
          body:
            'Birate model prema tehničkim mogućnostima vašeg sistema i dinamici tima, bez “jednog modela za sve”.',
          pillarLabels: {
            problem: 'Kada birate',
            solution: 'Model',
            outcome: 'Šta dobijate',
          },
          pillars: [
            {
              title: 'Direktna API integracija',
              problem: 'Kada vam je potreban programski i pouzdan tok podataka.',
              solution: 'Dogovaramo endpoint opseg, autentikaciju i mapiranje polja.',
              outcome: 'Stabilan tok bez ručnog posredovanja.',
            },
            {
              title: 'Scheduled eksport',
              problem: 'Kada vaš sistem koristi fajl-import kao standardan ulaz.',
              solution: 'Definišemo format, učestalost i validaciju eksporta.',
              outcome: 'Predvidiv ulaz za obračun i kontrolu.',
            },
            {
              title: 'Hibridni model',
              problem: 'Kada deo toka treba real-time, a deo periodično.',
              solution: 'Kombinujemo API i eksport prema poslovnom procesu.',
              outcome: 'Balans brzine i stabilnosti bez kompromisa.',
            },
          ],
        },
        {
          title: 'Kako izgleda partnerski onboarding',
          body:
            'Krećemo od minimuma koji daje vrednost, pa širimo opseg kada tok stabilno radi u realnom okruženju.',
          qa: [
            { question: '1) Uvodni poziv', answer: 'Use case, obim i očekivani izlaz za krajnjeg klijenta.' },
            { question: '2) Mapiranje podataka', answer: 'Polja, pravila i format razmene između sistema.' },
            { question: '3) Pilot integracija', answer: 'Test na ograničenom opsegu uz zajedničku validaciju.' },
            { question: '4) Produkcioni rollout', answer: 'Puštanje u rad i monitoring prvih ciklusa.' },
          ],
        },
        {
          title: 'Zašto partneri biraju ovaj pristup',
          body:
            'Cilj nije “još jedna integracija”, već brži i pouzdaniji rezultat za krajnjeg korisnika.',
          compare: {
            leftTitle: 'Bez jasne integracije',
            leftItems: [
              'dupli unos podataka',
              'ručno usklađivanje pre obračuna',
              'sporovi oko tačnih sati',
              'teško skaliranje na više lokacija',
            ],
            rightTitle: 'Sa Cadrino ERV integracijom',
            rightItems: [
              'jedan izvor operativne istine',
              'manje ručnih korekcija',
              'brži obračunski ciklus',
              'predvidiv proces za partnera i klijenta',
            ],
          },
        },
      ],
      finalCta: {
        title: 'Želite da vaš proizvod radi sa Cadrino ERV?',
        body:
          'Pošaljite kratak opis vašeg sistema i model integracije koji želite. Vraćamo se sa jasnim tehničkim predlogom i sledećim koracima.',
        primaryCta: 'Prijavite partnersku integraciju',
        qualifierChips: ['Tip sistema', 'Model integracije', 'Očekivani obim događaja'],
      },
    },
    security: {
      metadata: {
        title: 'Cadrino ERV Sigurnost, podaci pod kontrolom, bez crne kutije',
        description:
          'Vidite ko šta menja, gde podaci prolaze i šta se dešava kada veza stane. Cadrino ERV · sigurnost koju HR, operativa i rukovodstvo razumeju.',
      },
      teaser: {
        title: 'Sigurnost koju tim zaista razume',
        description:
          'Trag promena, izolacija podataka po firmi i jasan status kada mreža stane, bez nagađanja i bez crne kutije.',
        cta: 'Otvori Sigurnost',
      },
      hero: {
        eyebrow: 'Sigurnost bez kompromisa',
        title: 'Podaci pod kontrolom. Bez crne kutije.',
        subtitle:
          'Kada neko pita „da li je sigurno?“, odgovor mora biti jasan. Cadrino ERV pokazuje gde podaci prolaze, ko šta menja i šta se dešava kada veza ili uređaj zapnu, da HR, operativa i rukovodstvo rade sa istom slikom.',
        highlights: [
          'jasno ko je šta promenio',
          'podaci firme ostaju odvojeni',
          'ništa se ne gubi kada mreža stane',
        ],
        primaryCta: 'Zakažite sigurnosni demo',
        secondaryCta: 'Pogledajte Proizvod',
        secondaryHref: '/product',
      },
      sections: [
        {
          title: 'Šta vas najviše brine, i kako to rešavamo',
          body:
            'Sigurnost nije lista tehničkih reči. To je mir u glavi da znate šta se dešava u firmi, ko je odgovoran za promenu i da podaci ne nestaju iz priče.',
          standardsTable: {
            headers: ['Šta vas brine', 'Kako Cadrino ERV pomaže', 'Šta dobijate'],
            rows: [
              {
                criterion: 'Ko je šta promenio?',
                pain: 'Promene pristupa ili zaposlenih bez traga, rasprave bez kraja',
                help: 'Pregled aktivnosti: ko, kada i šta je menjao',
              },
              {
                criterion: 'Da li su podaci sačuvani?',
                pain: 'Nejasno šta je obrađeno, šta čeka, da li nešto nedostaje',
                help: 'Status obrade i red čekanja, vidljivo šta čeka i gde reagovati',
              },
              {
                criterion: 'Da li su uređaji izloženi?',
                pain: 'Strah da uređaji „žive“ na internetu bez kontrole',
                help: 'Veza preko programa u mreži firme, ne direktno sa uređaja na internet',
              },
              {
                criterion: 'Da li tim razume sistem?',
                pain: 'Samo IT zna šta status znači, ostali zovu podršku u panici',
                help: 'Jasni statusi i poruke za HR, operativu i rukovodstvo',
              },
            ],
          },
        },
        {
          title: 'Četiri stuba sigurnosti u praksi',
          body:
            'Svaki stub rešava jednu stvarnu bolnu tačku, od izolacije podataka do otpornosti kada veza stane.',
          pillarLabels: {
            problem: 'Problem',
            solution: 'Rešenje',
            outcome: 'Ishod',
          },
          pillars: [
            {
              title: 'Izolacija podataka po firmi',
              problem: 'Podaci se mešaju ili nije jasno ko šta vidi.',
              solution: 'Podaci vaše firme su odvojeni; pristup po kompaniji i ulozi.',
              outcome: 'Manje rizika, jasna odgovornost, poverenje u sistem.',
            },
            {
              title: 'Kontrola pristupa i promena',
              problem: 'Niko ne zna ko je promenio pristup ili zaposlenog.',
              solution: 'Trag promena za pristup, zaposlene i obradu podataka.',
              outcome: 'Kraće rasprave, manje grešaka, jasnija odgovornost.',
            },
            {
              title: 'Otpornost kada veza stane',
              problem: 'Panika kad internet padne, „da li smo izgubili evidenciju?“',
              solution: 'Događaji čekaju obradu i ostaju vidljivi dok se veza ne vrati.',
              outcome: 'Bez ručnog spašavanja podataka i bez praznine u izveštaju.',
            },
            {
              title: 'Operativna jasnoća, ne samo IT',
              problem: 'Statusi koje razume samo tehničar.',
              solution: 'Pregled koji HR i operativa koriste svakodnevno.',
              outcome: 'Brže reagovanje, manje poziva podršci, više kontrole.',
            },
          ],
        },
        {
          title: 'Zašto ozbiljne firme biraju ovakav model',
          body:
            'Ne traže još jednu „security“ oznaku na sajtu. Traže sistem u kome znaju šta se dešava pre nego što postane problem.',
          bullets: [
            'Znate šta je stvarno zabeleženo pre obračuna i obrade',
            'Vidite problem pre nego što postane haos na ulazu ili u izveštaju',
            'Tim ne mora da poznaje sistem da bi radio, statusi su razumljivi',
            'Sigurnost nije samo IT tema: HR, operativa i rukovodstvo imaju istu sliku',
          ],
        },
        {
          title: 'Pre i posle, kako izgleda svakodnevica',
          body:
            'Prepoznajete li se u levoj koloni? Desna je ono što firme žele kada uvedu Cadrino ERV.',
          compare: {
            leftTitle: 'Nejasno i stresno',
            leftItems: [
              'nejasni statusi, „da li je prošlo?“',
              'ručno proveravanje logova i tabela',
              'podaci nestaju iz priče kad veza stane',
              'promene bez odgovornosti i traga',
            ],
            rightTitle: 'Sa Cadrino ERV',
            rightItems: [
              'jasan status obrade i red čekanja',
              'pregled aktivnosti, ko je šta promenio',
              'događaji ostaju vidljivi dok se veza ne vrati',
              'operativni tim zna šta da uradi, bez IT prevodioca',
            ],
          },
        },
        {
          title: 'Česta pitanja o sigurnosti',
          body:
            'Odgovori koje timovi traže pre odluke, bez tehničkog žargona.',
          qa: [
            {
              question: 'Da li su podaci izolovani po firmama?',
              answer:
                'Da. Podaci jedne firme nisu pomešani sa drugom. Pristup i pregled su organizovani po vašoj kompaniji i ulozi.',
            },
            {
              question: 'Šta se dešava kad internet ili mreža padnu?',
              answer:
                'Događaji se čuvaju i ostaju vidljivi u sistemu dok se veza ne vrati. Ne morate da „spasavate“ evidenciju ručno.',
            },
            {
              question: 'Da li vidimo ko je šta promenio?',
              answer:
                'Da. Pregled aktivnosti pokazuje promene pristupa, zaposlenih i obrade, ko je i kada uradio izmenu.',
            },
            {
              question: 'Da li je ovo samo za IT odeljenje?',
              answer:
                'Ne. Cadrino ERV je napravljen da ga svakodnevno koriste HR, operativa i rukovodstvo, sa jasnim statusima, ne sa crnom kutijom.',
            },
          ],
        },
      ],
      finalCta: {
        title: 'Hoćete da vidite kako izgleda sigurnost u praksi?',
        body:
          'Na sigurnosnom demo-u prolazimo tok podataka (uređaji → obrada → tim), status kada veza stane, trag promena i plan uvođenja po vašim lokacijama, korak po korak, na vašem scenariju.',
        primaryCta: 'Zakažite sigurnosni demo',
      },
    },
  },
};

const enCopy: WebsitePagesCopy = {
  brand: 'Cadrino ERV',
  nav: {
    home: 'Home',
    product: 'Product',
    integrations: 'Cadrino HR',
    partners: 'Partners',
    security: 'Security',
    privacy: 'Privacy policy',
    terms: 'Terms of use',
    contact: 'Contact',
    signIn: 'Sign in',
    bookDemo: 'Book a demo',
  },
  pages: {
    product: {
      metadata: {
        title: 'Cadrino ERV Product, attendance, access, devices, and reports',
        description:
          'One system for attendance, access control, devices, and payroll-ready reports. Simple for your team, serious for your company.',
      },
      teaser: {
        title: 'The full ERV system without the complexity',
        description:
          'Attendance, access, devices, data processing, and key reports in one view your team actually uses.',
        cta: 'Open Product',
      },
      hero: {
        eyebrow: 'Product',
        title: 'Simple for your team. Serious for your company.',
        subtitle:
          'Cadrino ERV brings attendance, access control, and device operations into one clear system. HR, operations, and leadership see the same picture, every day, not only at month-end.',
        highlights: [
          'attendance and access in one place',
          'devices under control in real time',
          'reports ready for payroll',
          'turnkey or your existing infrastructure',
        ],
        primaryCta: 'Book a product demo',
        secondaryCta: 'See Cadrino HR',
        secondaryHref: '/integrations',
      },
      sections: [
        {
          title: 'What it costs without one operational view',
          body:
            'Most companies have devices and some form of attendance, but not answers to the questions asked every day. Cadrino ERV introduces one truth for the whole team.',
          officePresencePreview: {
            title: 'Currently in office',
            subtitle: 'IN without OUT event',
            countLabel: 'currently present',
            people: ['James Mitchell', 'Sarah Collins', 'David Brooks', 'Rachel Foster'],
          },
          standardsTable: {
            headers: ['Daily question', 'Without one system', 'With Cadrino ERV'],
            rows: [
              {
                criterion: 'Who is present today?',
                pain: 'Checking multiple tables, messages, and assumptions',
                help: 'Clear attendance view by location, team, or device',
              },
              {
                criterion: 'Who may use which entrance?',
                pain: 'Access too broad or wrong, errors and risk',
                help: 'Rules by employee, device, entrance, or location',
              },
              {
                criterion: 'Is the device working?',
                pain: 'Problems surface only when the report “does not add up”',
                help: 'Device status and processing queue, visible immediately',
              },
              {
                criterion: 'What goes to payroll?',
                pain: 'Manual data merge before payroll',
                help: 'Reports and export from one source, without spreadsheet chaos',
              },
            ],
          },
        },
        {
          title: 'What Cadrino ERV covers in one system',
          body:
            'We are not selling “another time clock”. We sell control over attendance, access, and devices, with clear processing and reports your team actually uses.',
          pillarLabels: {
            problem: 'What hurts',
            solution: 'What Cadrino ERV does',
            outcome: 'What you get',
          },
          pillars: [
            {
              title: 'Attendance records',
              problem: 'Arrivals and departures are checked in multiple places.',
              solution: 'All device events flow into one view with clear processing status.',
              outcome: 'Less manual work and fewer disputes over hours.',
            },
            {
              title: 'Access control',
              problem: 'Some people have access everywhere, others nowhere, chaos when things change.',
              solution: 'Define who may use which devices, entrances, and locations.',
              outcome: 'Order at the entrance, fewer security and organizational mistakes.',
            },
            {
              title: 'Devices under control',
              problem: 'You do not know a device is down until the report hurts.',
              solution: 'See device status, last connection, and what was recorded.',
              outcome: 'You react before gaps appear in attendance data.',
            },
            {
              title: 'Software in your network',
              problem: 'Fear that devices “live” on the internet or that data disappears.',
              solution: 'Connection through software in your company; events queue if connectivity fails.',
              outcome: 'Control stays with you, without a black box.',
            },
            {
              title: 'Processing and queue',
              problem: '“Did it go through?”, nobody knows what is waiting or why.',
              solution: 'A clear event queue: processed, waiting, needs review.',
              outcome: 'The team knows where to react, without an IT translator.',
            },
            {
              title: 'Multiple locations, one view',
              problem: 'Each location has its own spreadsheet and its own truth.',
              solution: 'The same system for every site, entrance, and device in the company.',
              outcome: 'Leadership and HR see the full picture, not fragments.',
            },
          ],
        },
        {
          title: 'The view your team opens every day',
          body:
            'Cadrino ERV is not a “month-end system”. It is a tool for operations, HR, and leadership throughout the day.',
          quickStats: [
            {
              label: 'Late arrivals today',
              value: '11',
              hint: 'employees 5+ min late',
            },
            {
              label: 'Early departures',
              value: '6',
              hint: 'left before shift end',
            },
            {
              label: 'Events waiting',
              value: '4',
              hint: 'retry or review needed',
            },
            {
              label: 'Out-of-policy entries',
              value: '3',
              hint: 'access attempts without permission',
            },
          ],
          cards: [
            {
              title: 'Devices',
              description:
                'Where they are, whether they work, when they last checked in, and what was recorded, without calling IT.',
            },
            {
              title: 'Who may go where',
              description:
                'Access rights by employee, device, or location. Changes are visible, not hidden in a spreadsheet.',
            },
            {
              title: 'What is waiting to process',
              description:
                'Events waiting for processing, resend, or review, so nobody guesses whether attendance went through.',
            },
          ],
        },
        {
          title: 'Do not trust the report alone: see what was recorded',
          body:
            'When the device and the report disagree, the problem does not disappear. Compare actual state with the view before payroll and fix differences while they are still small.',
          bullets: [
            'Check before payroll, not after the argument',
            'Less “whose number is correct?” at month-end',
            'Same base for operations, HR, and leadership',
          ],
          payrollReportPreview: {
            badge: 'Payroll-ready report',
            title: 'Monthly worked-hours summary',
            subtitle: 'Format HR and accounting can use immediately for salary calculations.',
            periodLabel: 'Period',
            periodValue: 'May 2026',
            colEmployee: 'Employee',
            colRegular: 'Regular',
            colOvertime: 'Overtime',
            colNet: 'Net hours',
            rows: [
              { employee: 'James Mitchell', regularHours: '176h', overtimeHours: '8h', netHours: '184h' },
              { employee: 'Sarah Collins', regularHours: '168h', overtimeHours: '4h', netHours: '172h' },
              { employee: 'David Brooks', regularHours: '174h', overtimeHours: '6h', netHours: '180h' },
            ],
            totalLabel: 'Total net hours',
            totalValue: '536h',
            statusLabel: 'Ready to send to accounting',
          },
        },
        {
          title: 'Reports that close the month without merging spreadsheets',
          body:
            'Beyond the daily view, you get reports HR and finance actually use. We list the key ones; in practice there are many more, including custom reports for your company.',
          reportsWorkbenchPreview: {
            badge: 'Payroll workbench',
            title: 'Review before sending to accounting',
            subtitle: 'One screen for final checks and payroll export, without manual source merging.',
            valueTitle: 'Additional value',
            metrics: [
              { label: 'Late arrivals (month)', value: '37' },
              { label: 'Early departures', value: '19' },
              { label: 'Night hours', value: '42h' },
              { label: 'Overtime hours', value: '18h' },
            ],
            exportLabel: 'Payroll export',
            exportFormats: ['XLSX', 'CSV', 'PDF'],
            readyStatus: 'Ready to send to accounting',
          },
          cardsColumns: 2,
          cards: [
            {
              title: 'Monthly timesheet and attendance summary',
              description:
                'Overview of arrivals, departures, and hours by employee and period, ready for further processing and payroll.',
            },
            {
              title: 'Late arrivals, early departures, and absences',
              description: 'Spot patterns before they become disputes over hours or discipline.',
            },
            {
              title: 'Attendance and access by location',
              description: 'Who was where and who had entry rights, by site, entrance, or device.',
            },
            {
              title: 'Export for payroll and archive',
              description:
                'Data in a format suited for HR, finance, and internal control, without copying from multiple sources.',
            },
          ],
          bullets: [
            'and many other standard reports for daily and monthly work',
            'custom reports on client request, when you need a specific view of your data',
            'analytics and additional views, we expand the offering as companies need them',
          ],
        },
        {
          title: 'Two ways to start (both with full support)',
          body:
            'We choose the model based on your actual situation. On a demo we tell you honestly what you need, without selling what you will not use.',
          useLandingDeploymentStyle: true,
          cardsColumns: 2,
          cards: [
            {
              title: 'Turnkey',
              description:
                'Complete solution: latest-generation devices, software, network, rollout, and handover. Ideal when starting from scratch or wanting everything from one source.',
            },
            {
              title: 'Existing infrastructure',
              description:
                'Cadrino ERV on your devices and network. Automate attendance and access without replacing everything at once, expand when the team is ready.',
            },
          ],
          bullets: [
            'free training after go-live',
            'free customer and technical support',
            'optional connection with Cadrino HR when you need the full HR flow',
          ],
        },
        {
          title: 'Why companies choose Cadrino ERV',
          body:
            'Companies choose us when they want to stop money leakage from late arrivals, wrong access rules, and manual payroll preparation. These are the four areas where the impact is most visible.',
          pillarLabels: {
            problem: 'Where money leaks',
            solution: 'How Cadrino ERV fixes it',
            outcome: 'Why this beats generic tools',
          },
          pillars: [
            {
              title: 'Late arrivals and early departures',
              problem: 'Small daily deviations silently add up and are noticed too late.',
              solution: 'Late arrivals, early departures, and attendance patterns are visible immediately by team and site.',
              outcome: 'You act during the month, not only when payroll exposes the gap.',
            },
            {
              title: 'Access rules that create risk',
              problem: 'Overly broad or wrong access creates incidents, delays, and security exposure.',
              solution: 'Access rules are clear by employee, device, entrance, and location, with change traceability.',
              outcome: 'Fewer incidents and fewer ad-hoc access exceptions that nobody tracks later.',
            },
            {
              title: 'Manual work before payroll',
              problem: 'HR and finance lose hours merging spreadsheets before each payroll cycle.',
              solution: 'One trusted data source with payroll-ready reports and export, no manual bridge.',
              outcome: 'Faster month close and fewer costly payroll errors.',
            },
            {
              title: 'A system teams avoid',
              problem: 'When tools are hard to use, operations fall back to chat messages and spreadsheets.',
              solution: 'The interface is practical for day-to-day work across HR, operations, and leadership.',
              outcome: 'The system is actually used every day, so data stays accurate and actionable.',
            },
          ],
        },
        {
          title: 'Before and after: is this the system for you?',
          body:
            'If you recognize the left column, the right is what companies want when they adopt Cadrino ERV.',
          compare: {
            leftTitle: 'Data chaos',
            leftItems: [
              'spreadsheets, messages, and “check the device”',
              'access changes manually and errors show up late',
              'device down, discovered at month-end',
              'month-end = late-night spreadsheet merge',
              'late arrivals and early departures stay under the radar',
              'overtime reaches payroll without clear control',
              'leadership gets numbers only when it is too late to react',
            ],
            rightTitle: 'Cadrino ERV',
            rightItems: [
              'one view: attendance, access, devices',
              'clear rules and traceable changes',
              'device status and queue immediately',
              'reports and export without a manual bridge',
              'late arrivals and departures visible during the month, not only at close',
              'worked-hours calculation based on auditable data, with fewer payroll disputes',
              'leadership tracks trends and acts before costs escalate',
            ],
          },
        },
        {
          title: 'The standard a serious company checks before deciding',
          body:
            'Before you choose a system, ask these questions. Cadrino ERV is designed so you can answer yes with confidence.',
          anchorId: 'standard',
          standardsTable: {
            headers: ['Criterion', 'Typical situation', 'How Cadrino ERV helps'],
            rows: [
              {
                criterion: 'Who may go where',
                pain: 'Access by guesswork or too broad',
                help: 'Rules by device, entrance, location',
              },
              {
                criterion: 'What was recorded',
                pain: 'Report and device do not match',
                help: 'Compare and verify before payroll',
              },
              {
                criterion: 'Device status',
                pain: 'Failures noticed too late',
                help: 'Device and processing view in real time',
              },
              {
                criterion: 'Reports',
                pain: 'Manual merge before payroll',
                help: 'Timesheet, lateness, export, and custom reports on request',
              },
              {
                criterion: 'Rollout',
                pain: '“We will manage” with no plan or support',
                help: 'Demo, plan, training, and free support after go-live',
              },
            ],
          },
        },
        {
          title: 'Common product questions',
          body: 'Answers before the demo, direct and without fluff.',
          qa: [
            {
              question: 'Is the system complicated for daily use?',
              answer:
                'No. The focus is a clear view that operations and HR use every day, without technical jargon. On a demo we walk through your scenario step by step.',
            },
            {
              question: 'What if we already have devices?',
              answer:
                'We introduce Cadrino ERV on your existing infrastructure. Replacing all equipment on day one is not required, expand when you see value.',
            },
            {
              question: 'Which reports do we get?',
              answer:
                'Timesheet and attendance summary, lateness and absences, views by location, export for payroll, plus many other standard reports and custom reports on request.',
            },
            {
              question: 'Do we need Cadrino HR from day one?',
              answer:
                'No. ERV works on its own. Integration with Cadrino HR is optional when you want one flow from attendance to HR and payroll.',
            },
            {
              question: 'Can we start with one location?',
              answer:
                'Yes. Many companies start with one part of the business and expand when the team sees results, without disrupting operations.',
            },
          ],
        },
      ],
      finalCta: {
        title: 'Want to see if Cadrino ERV is ideal for you?',
        body:
          'In a product demo we walk through your locations, devices, access rules, and the reports you need, with a realistic rollout plan, free training, and free customer and technical support after go-live.',
        primaryCta: 'Book a product demo',
      },
    },
    integrations: {
      metadata: {
        title: 'Cadrino ERV + HR, one product, one data flow',
        description:
          'Attendance, access, and devices with Cadrino ERV. People, absences, and payroll prep with Cadrino HR. Connected, without duplicate entry or manual copying.',
      },
      teaser: {
        title: 'ERV and HR as one product',
        description:
          'One operational flow from the entrance to payroll, people, absences, and attendance on the same verifiable base.',
        cta: 'Open Cadrino HR',
      },
      hero: {
        eyebrow: 'Cadrino HR',
        title: 'One product. Two parts. One flow.',
        subtitle:
          'Cadrino ERV runs attendance, access, and devices on the floor. Cadrino HR runs people records, contracts, absences, and documentation. When connected, they work as one system, without spreadsheets between operations and HR.',
        highlights: [
          'one data flow',
          'no duplicate entry',
          'ERV first, HR when you are ready',
        ],
        primaryCta: 'Book an ERV + HR demo',
        secondaryCta: 'See Product',
        secondaryHref: '/product',
      },
      sections: [
        {
          title: 'What it costs when systems do not work together',
          body:
            'The most common problem is not missing features, it is operations and HR looking at different data and manually merging the truth at month-end.',
          standardsTable: {
            headers: ['Pain point', 'How ERV + HR help', 'What you get'],
            rows: [
              {
                criterion: 'Duplicate employee entry',
                pain: 'The same person in multiple tables, errors and wasted time',
                help: 'People and structure enter once and appear in both systems',
              },
              {
                criterion: 'Absences attendance does not know',
                pain: 'Approved in HR, different at the entrance and in reports',
                help: 'Approved absences are respected in attendance, fewer corrections',
              },
              {
                criterion: 'Month-end = manual merge',
                pain: 'Spreadsheets, messages, “whose number is correct?”',
                help: 'Device attendance flows into HR on the same base',
              },
              {
                criterion: 'Two teams, two truths',
                pain: 'Operations and HR do not trust the same source',
                help: 'One operational flow, the same picture for everyone',
              },
            ],
          },
        },
        {
          title: 'One flow: from the entrance to payroll',
          body:
            'Each step removes one real barrier between the floor and HR, without extra administrative work.',
          pillarLabels: {
            problem: 'Without connection',
            solution: 'With ERV + HR',
            outcome: 'Outcome',
          },
          pillars: [
            {
              title: 'People and structure',
              problem: 'New hires are entered in multiple places; data drifts apart.',
              solution: 'One entry, the same person in attendance and HR modules.',
              outcome: 'Fewer errors at the entrance and in reports.',
            },
            {
              title: 'Absences',
              problem: 'Sick leave approved in HR, but attendance still shows presence.',
              solution: 'Absences are applied automatically where attendance needs them.',
              outcome: 'Fewer disputes and manual fixes between teams.',
            },
            {
              title: 'Attendance → payroll',
              problem: 'Device data is manually moved into HR before payroll.',
              solution: 'What was recorded on devices reaches HR ready for further processing.',
              outcome: 'Faster month-end, less administration.',
            },
            {
              title: 'Rollout at your pace',
              problem: 'Fear that every system must go live at once.',
              solution: 'Start with ERV; connect HR when you are ready.',
              outcome: 'No disruption and no pressure for a “big bang” migration.',
            },
          ],
        },
        {
          title: 'Who owns what and how it connects',
          body:
            'Two clear parts of one product. Operations on the floor, HR in the office, the same truth in the background.',
          cards: [
            {
              title: 'Cadrino ERV',
              description:
                'Attendance, access, devices, and processing status in real time. Focus of this site, can run standalone without an HR bundle.',
            },
            {
              title: 'Cadrino HR',
              description:
                'People, contracts, absences, documentation, and payroll preparation. Full HR module at cadrinohr.com.',
            },
            {
              title: 'Together',
              description:
                'Automatic exchange of people, absences, and attendance. One data flow, less duplicate entry, fewer errors, faster payroll.',
            },
          ],
        },
        {
          title: 'Why companies choose ERV + HR as one product',
          body:
            'They are not looking for another integration on paper. They want operations and HR to stop copying data and want month-end to be predictable.',
          bullets: [
            'Operations runs attendance; HR runs people and payroll, without a manual bridge',
            'Absences and presence on the same verifiable base',
            'Device attendance flows forward without “merging spreadsheets”',
            'Start with ERV; connect HR when there is a real need, not required on day one',
          ],
        },
        {
          title: 'Before and after, what daily work looks like',
          body:
            'On the left is what most companies recognize. On the right is what you get when ERV and HR work as one product.',
          compare: {
            leftTitle: 'Separate systems',
            leftItems: [
              'people in multiple tables and files',
              'absences in HR, attendance unaware',
              'month-end = spreadsheets and messages',
              'operations and HR do not trust the same number',
            ],
            rightTitle: 'ERV + HR, one flow',
            rightItems: [
              'people entered once, visible where needed',
              'absences respected in attendance',
              'attendance ready for HR and payroll',
              'same picture for operations, HR, and leadership',
            ],
          },
        },
        {
          title: 'Common integration questions',
          body: 'Answers before a decision, what is required and what is optional.',
          qa: [
            {
              question: 'Do I need Cadrino HR from day one?',
              answer:
                'No. Cadrino ERV works on its own for attendance, access, and devices. You add the Cadrino HR connection when the company needs the full HR flow.',
            },
            {
              question: 'What exactly flows from ERV to HR?',
              answer:
                'People and structure, absences where they matter for attendance, and device data ready for further HR processing, without manual copying.',
            },
            {
              question: 'Can we start with ERV only and add HR later?',
              answer:
                'Yes. That is a common path: operational layer first, HR connection when the team sees value and you are ready for the next step.',
            },
            {
              question: 'Is this “another integration” that breaks?',
              answer:
                'We are not selling a third-party integration on paper. ERV and HR are part of the same product family, designed to work together as one flow, not as two disconnected tools.',
            },
          ],
        },
      ],
      finalCta: {
        title: 'Want to see ERV and HR as one product in your company?',
        body:
          'In an ERV + HR demo we walk through your scenario: devices and rules in ERV, what goes to HR, how people and absences flow, and a rollout plan, step by step, with no requirement to adopt HR on day one.',
        primaryCta: 'Book an ERV + HR demo',
      },
    },
    partners: {
      metadata: {
        title: 'Partners and integrations, connect your system to Cadrino ERV',
        description:
          'If you build accounting software, ERP, or internal business systems, connect them to Cadrino ERV without duplicate entry and manual copy/paste.',
      },
      teaser: {
        title: 'Partner ecosystem for accounting and ERP',
        description:
          'One operational source of truth from ERV for payroll, control, and automation in your product.',
        cta: 'Open Partners',
      },
      hero: {
        eyebrow: 'Partners and Integrations',
        title: 'Connect Cadrino ERV to your system. No duplicate entry.',
        subtitle:
          'If you build accounting software, ERP, or an internal business app, Cadrino ERV can be your reliable source for attendance, access, and device events.',
        highlights: ['accounting software', 'ERP systems', 'internal apps', 'API and export models'],
        primaryCta: 'Submit a partner integration',
        secondaryCta: 'See Product',
        secondaryHref: '/product',
      },
      sections: [
        {
          title: 'Who typically integrates with Cadrino ERV',
          body:
            'The biggest value comes to teams that want an automated flow from attendance events to payroll outputs, without manual handoffs.',
          cards: [
            {
              title: 'Accounting software',
              description: 'Pull payroll-relevant data without manual entry and spreadsheet reconciliation.',
            },
            {
              title: 'ERP systems',
              description: 'Connect attendance and organizational data to your existing ERP workflows.',
            },
            {
              title: 'Internal business solutions',
              description: 'Integrate to your own data model, formats, and synchronization rhythm.',
            },
          ],
        },
        {
          title: 'Data your teams can actually use',
          body:
            'This is not a raw log dump. The integration scope is focused on data useful for payroll, operations, and audit.',
          bullets: [
            'IN/OUT events with precise timestamps',
            'processing status per event (processed, waiting, review needed)',
            'aggregates by employee, period, and location',
            'exception signals: late arrivals, early exits, irregular patterns',
            'payroll-ready export structures',
          ],
        },
        {
          title: 'Technical snapshot, required vs optional',
          body:
            'Before implementation we define the minimum pilot scope and optional next steps, so effort and timeline stay predictable.',
          technicalSnapshot: {
            badge: 'Technical snapshot',
            title: 'Fast scoping without technical guesswork',
            points: [
              'Required: auth, employee mapping, event timestamp, and event type.',
              'Optional: real-time stream, extended attributes, BI export, and extra audit metadata.',
              'Pilot scope: one location, one payroll period, agreed output format.',
            ],
            sampleTitle: 'Example event payload',
            sampleLines: [
              '{',
              '  "employeeId": "EMP-1042",',
              '  "eventType": "IN",',
              '  "eventAt": "2026-05-28T07:58:14Z",',
              '  "deviceId": "dev-main-01",',
              '  "location": "Novi Sad - Main entrance"',
              '}',
            ],
          },
        },
        {
          title: 'Integration model based on your product',
          body:
            'Choose the model based on your system capabilities and team speed, not a forced one-size-fits-all approach.',
          pillarLabels: {
            problem: 'When to choose',
            solution: 'Model',
            outcome: 'What you get',
          },
          pillars: [
            {
              title: 'Direct API integration',
              problem: 'When you need programmatic and reliable data flow.',
              solution: 'We define endpoint scope, auth, and field mapping together.',
              outcome: 'Stable flow without manual handoff.',
            },
            {
              title: 'Scheduled export',
              problem: 'When your system is based on file import workflows.',
              solution: 'We define format, cadence, and validation rules.',
              outcome: 'Predictable input for payroll and controls.',
            },
            {
              title: 'Hybrid model',
              problem: 'When part of the flow is real-time and part periodic.',
              solution: 'We combine API and export by business scenario.',
              outcome: 'Balanced speed and stability.',
            },
          ],
        },
        {
          title: 'What partner onboarding looks like',
          body:
            'We start with the minimum flow that delivers value, then expand scope after stable validation in a real environment.',
          qa: [
            { question: '1) Discovery call', answer: 'Use case, scope, and expected output for your shared client.' },
            { question: '2) Data mapping', answer: 'Fields, rules, and exchange format between systems.' },
            { question: '3) Pilot integration', answer: 'Limited-scope test with joint validation.' },
            { question: '4) Production rollout', answer: 'Go-live and monitoring through the first real cycles.' },
          ],
        },
        {
          title: 'Why partners choose this approach',
          body: 'The goal is not “just another integration”, it is faster, cleaner value for the end client.',
          compare: {
            leftTitle: 'Without a clear integration',
            leftItems: [
              'duplicate data entry',
              'manual reconciliation before payroll',
              'disputes around “correct” hours',
              'hard scaling across locations',
            ],
            rightTitle: 'With Cadrino ERV integration',
            rightItems: [
              'one operational source of truth',
              'fewer manual corrections',
              'faster payroll cycle',
              'predictable process for partner and client',
            ],
          },
        },
      ],
      finalCta: {
        title: 'Want your product to work with Cadrino ERV?',
        body:
          'Send a short description of your system and preferred integration model. We come back with a clear technical proposal and next steps.',
        primaryCta: 'Submit a partner integration',
        qualifierChips: ['System type', 'Integration model', 'Expected event volume'],
      },
    },
    security: {
      metadata: {
        title: 'Cadrino ERV Security, data under control, no black box',
        description:
          'See who changed what, where data flows, and what happens when connectivity fails. Security HR, operations, and leadership can actually use.',
      },
      teaser: {
        title: 'Security your team can actually understand',
        description:
          'Traceable changes, company data isolation, and clear status when the network stops, without guesswork and without a black box.',
        cta: 'Open Security',
      },
      hero: {
        eyebrow: 'Security without compromise',
        title: 'Data under control. No black box.',
        subtitle:
          'When someone asks “is it secure?”, the answer must be clear. Cadrino ERV shows where data flows, who changed what, and what happens when connectivity or a device fails, so HR, operations, and leadership work from the same picture.',
        highlights: [
          'clear who changed what',
          'company data stays isolated',
          'nothing is lost when the network stops',
        ],
        primaryCta: 'Book a security demo',
        secondaryCta: 'See Product',
        secondaryHref: '/product',
      },
      sections: [
        {
          title: 'What keeps you up at night, and how we address it',
          body:
            'Security is not a list of technical terms. It is peace of mind that you know what is happening in the company, who is accountable for a change, and that data does not disappear from the story.',
          standardsTable: {
            headers: ['What you worry about', 'How Cadrino ERV helps', 'What you get'],
            rows: [
              {
                criterion: 'Who changed what?',
                pain: 'Access or employee changes with no trace, endless discussions',
                help: 'Activity view: who changed what and when',
              },
              {
                criterion: 'Is data saved?',
                pain: 'Unclear what was processed, what is waiting, whether something is missing',
                help: 'Processing status and queue, visible what waits and where to react',
              },
              {
                criterion: 'Are devices exposed?',
                pain: 'Fear that devices “live” on the internet without control',
                help: 'Connection through software in your network, not devices exposed directly online',
              },
              {
                criterion: 'Does the team understand the system?',
                pain: 'Only IT knows what a status means, everyone else calls support in panic',
                help: 'Clear statuses and messages for HR, operations, and leadership',
              },
            ],
          },
        },
        {
          title: 'Four security pillars in practice',
          body:
            'Each pillar solves one real pain point, from data isolation to resilience when connectivity fails.',
          pillarLabels: {
            problem: 'Problem',
            solution: 'Solution',
            outcome: 'Outcome',
          },
          pillars: [
            {
              title: 'Company data isolation',
              problem: 'Data gets mixed or it is unclear who sees what.',
              solution: 'Your company data stays separate; access by company and role.',
              outcome: 'Less risk, clear accountability, trust in the system.',
            },
            {
              title: 'Access and change control',
              problem: 'Nobody knows who changed access or an employee record.',
              solution: 'A trace for access, employees, and data processing changes.',
              outcome: 'Shorter discussions, fewer mistakes, clearer accountability.',
            },
            {
              title: 'Resilience when connectivity fails',
              problem: 'Panic when the internet drops, “did we lose attendance data?”',
              solution: 'Events queue and stay visible until connectivity returns.',
              outcome: 'No manual data rescue and no gaps in the report.',
            },
            {
              title: 'Operational clarity, not IT-only',
              problem: 'Statuses only a technician understands.',
              solution: 'A view HR and operations use every day.',
              outcome: 'Faster response, fewer support calls, more control.',
            },
          ],
        },
        {
          title: 'Why serious companies choose this model',
          body:
            'They are not looking for another “security” badge on a website. They want a system where they know what is happening before it becomes a problem.',
          bullets: [
            'You know what was actually recorded before payroll and processing',
            'You see problems before they become chaos at the entrance or in reports',
            'The team does not need to know the system inside out, statuses are understandable',
            'Security is not IT-only: HR, operations, and leadership share the same picture',
          ],
        },
        {
          title: 'Before and after, what daily work looks like',
          body:
            'Do you recognize the left column? The right is what companies want when they adopt Cadrino ERV.',
          compare: {
            leftTitle: 'Unclear and stressful',
            leftItems: [
              'unclear statuses, “did it go through?”',
              'manual checking of logs and spreadsheets',
              'data disappears from the story when connectivity fails',
              'changes with no accountability or trace',
            ],
            rightTitle: 'With Cadrino ERV',
            rightItems: [
              'clear processing status and queue',
              'activity view, who changed what',
              'events stay visible until connectivity returns',
              'operations knows what to do without an IT translator',
            ],
          },
        },
        {
          title: 'Common security questions',
          body: 'Answers teams ask before a decision, without technical jargon.',
          qa: [
            {
              question: 'Is data isolated by company?',
              answer:
                'Yes. One company’s data is not mixed with another’s. Access and visibility are organized around your company and role.',
            },
            {
              question: 'What happens when the internet or network fails?',
              answer:
                'Events are stored and stay visible in the system until connectivity returns. You do not need to manually “rescue” attendance data.',
            },
            {
              question: 'Can we see who changed what?',
              answer:
                'Yes. The activity view shows changes to access, employees, and processing, who made the change and when.',
            },
            {
              question: 'Is this only for the IT department?',
              answer:
                'No. Cadrino ERV is built for daily use by HR, operations, and leadership, with clear statuses, not a black box.',
            },
          ],
        },
      ],
      finalCta: {
        title: 'Want to see what security looks like in practice?',
        body:
          'In a security demo we walk through data flow (devices → processing → team), status when connectivity fails, change traceability, and a rollout plan for your locations, step by step, on your scenario.',
        primaryCta: 'Book a security demo',
      },
    },
  },
};

export const cadrinoWebsitePagesCopy = {
  sr: srCopy,
  en: enCopy,
};

export type CadrinoWebsitePagesCopy = typeof srCopy;
